import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { usePermission } from '../../context/PermissionContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/client';
import SidebarEditorModal from '../SidebarEditorModal';

// 기본 메뉴 구조 — system_settings.sidebar_config 에 사용자 정의 값이 없을 때 폴백
const defaultMenuItems = [
  { id: 'main', title: '메인', items: [
    { path: '/', label: '대시보드', icon: '🖥️', menu: '대시보드' },
  ]},
  { id: 'estimate', title: '견적', items: [
    { path: '/product-estimate', label: '제품 견적서', icon: '📦', menu: '제품 견적서' },
    { path: '/service-estimate', label: '서비스견적서', icon: '🧑‍💻', menu: '서비스견적서' },
    { path: '/approvals', label: '승인상태', icon: '🙏', badgeKey: 'pendingApprovals', menu: '승인상태' },
    { path: '/bundles', label: '제품 묶음', icon: '🔗', menu: '제품 묶음' },
  ]},
  { id: 'material', title: '자재', items: [
    { path: '/products', label: '상품 관리', icon: '🎁', menu: '상품 관리' },
    { path: '/parts', label: '자재 관리', icon: '🧰', menu: '자재 관리' },
    { path: '/services', label: '서비스 관리', icon: '⛓️', menu: '서비스 관리' },
    { path: '/survey-jobs', label: '직무별 가격 관리', icon: '🏷️', menu: '직무별 가격 관리' },
  ]},
  { id: 'sales', title: '영업', items: [
    { path: '/customers', label: '고객 관리', icon: '📇', badgeKey: 'incompleteCustomers', menu: '고객 관리' },
  ]},
  { id: 'admin', title: '관리', items: [
    { path: '/organization', label: '조직 관리', icon: '🏢', menu: '조직 관리' },
    { path: '/users', label: '사용자관리', icon: '👩🏻‍💼', menu: '사용자관리' },
    { path: '/audit-logs', label: '감사 로그', icon: '📜', menu: '감사 로그' },
    { path: '/settings', label: '설정', icon: '⚙️', menu: '설정' },
  ]},
];

// 사용 안 함 처리된 경로 — saved config 에 남아있어도 사이드바에서 제거
const DEPRECATED_PATHS = new Set([
  '/settings/manpower-grades',  // 직무·등급 관리(/survey-jobs)로 통합
  '/itsqf-jobs',                // /survey-jobs 로 경로 변경
  '/estimates',                 // → /product-estimate
  '/service-estimates',         // → /service-estimate
  '/service-packages',          // → /services
  '/analytics',                 // 사용 안 함
]);

// path 키워드로 default 와 병합 — 사용자가 저장한 config 에 path/menu/badgeKey 가 손실되었어도 default 에서 복구
// + 새로 추가된 default 항목(서비스 패키지, 인력등급 등)도 자동 노출
// + DEPRECATED_PATHS 는 제거
function mergeWithDefaults(saved) {
  if (!Array.isArray(saved) || saved.length === 0) return defaultMenuItems;
  const defaultItemMap = new Map();
  defaultMenuItems.forEach(s => s.items.forEach(it => defaultItemMap.set(it.path, it)));
  const savedPaths = new Set();
  saved.forEach(s => (s.items || []).forEach(it => { if (it.path && !DEPRECATED_PATHS.has(it.path)) savedPaths.add(it.path); }));

  // 1) saved 의 각 섹션을 default 와 병합 (deprecated 경로 제외)
  const merged = saved.map(section => ({
    ...section,
    items: (section.items || [])
      .filter(it => !DEPRECATED_PATHS.has(it.path))
      .map(it => {
        const def = defaultItemMap.get(it.path) || {};
        // menu 속성은 권한 키와 1:1 — default(코드)값을 항상 우선해 구 키 흔적 제거
        return { ...def, ...it, path: it.path || def.path, menu: def.menu || it.menu, badgeKey: it.badgeKey || def.badgeKey };
      }),
  }));

  // 2) default 에 있지만 saved 에 없는 항목 → 같은 id 섹션에 append, 섹션 없으면 새로 추가
  for (const defSection of defaultMenuItems) {
    const missing = (defSection.items || []).filter(it => it.path && !savedPaths.has(it.path));
    if (missing.length === 0) continue;
    const target = merged.find(s => s.id === defSection.id || s.title === defSection.title);
    if (target) target.items = [...(target.items || []), ...missing];
    else merged.push({ ...defSection, items: missing });
  }
  return merged;
}

// 메뉴 항목별 활성 판정 — /estimates/print 같은 utility 경로는 메뉴에 매칭 안 됨
function isMenuActive(itemPath, currentPath) {
  if (itemPath === '/') return currentPath === '/';
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

export default function Sidebar({ collapsed, onToggle, onMenuItemsChange }) {
  const { canAccessMenu } = usePermission();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.uid === 'U-0000';
  const [pendingCount, setPendingCount] = useState(0);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [menuItems, setMenuItems] = useState(defaultMenuItems);
  const [logo, setLogo] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    const load = () => Promise.all([
      api.get('/approvals/pending').catch(() => []),
      api.get('/share-requests/incoming/count').catch(() => ({ count: 0 })),
      api.get('/customers/incomplete/count').catch(() => ({ count: 0 })),
    ]).then(([approvals, sr, inc]) => {
      setPendingCount((approvals?.length || 0) + (sr?.count || 0));
      setIncompleteCount(inc?.count || 0);
    });
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    api.get('/settings/sidebar_config')
      .then(r => {
        const merged = mergeWithDefaults(r?.value);
        setMenuItems(merged);
        onMenuItemsChange && onMenuItemsChange(merged);
      })
      .catch(() => {
        setMenuItems(defaultMenuItems);
        onMenuItemsChange && onMenuItemsChange(defaultMenuItems);
      });
    api.get('/settings/sidebar_logo')
      .then(r => setLogo(r?.value || null))
      .catch(() => setLogo(null));
  }, [onMenuItemsChange]);

  const badges = { pendingApprovals: pendingCount, incompleteCustomers: incompleteCount };
  // 접힘 상태에서 hover 하면 펼친 모습으로 보이게. 부모(main-content)의 margin 은 그대로라 오버레이됨.
  const showCollapsed = collapsed && !hovered;

  const handleSaved = (newConfig) => {
    const next = newConfig === null ? defaultMenuItems : mergeWithDefaults(newConfig);
    setMenuItems(next);
    onMenuItemsChange && onMenuItemsChange(next);
  };

  return (
    <nav className={`sidebar${showCollapsed ? ' collapsed' : ''}`}
      onMouseEnter={() => { if (collapsed) setHovered(true); }}
      onMouseLeave={() => setHovered(false)}>
      <div className="sidebar-inner" style={{ display:'flex', flexDirection:'column', minHeight:'100%' }}>
        <div className="sidebar-logo">
          <span className="sidebar-icon-col">
            <svg className="sidebar-menu-btn" onClick={onToggle} xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 -960 960 960" width="22" fill="currentColor"><path d="M120-240v-80h720v80H120Zm0-200v-80h720v80H120Zm0-200v-80h720v80H120Z"/></svg>
          </span>
          <span className="sidebar-logo-text">
            {logo?.type === 'image' && logo.image_url ? (
              <img src={logo.image_url} alt="logo" height={28} style={{ height:28, width:'auto', maxWidth:160, display:'inline-block', verticalAlign:'middle' }} />
            ) : logo?.type === 'text' && (logo.line1 || logo.line2) ? (
              <span style={{ display:'inline-flex', flexDirection:'column', lineHeight:1.1, verticalAlign:'middle' }}>
                {logo.line1 && <span style={{ color: logo.line1_color || '#60a5fa' }}>{logo.line1}</span>}
                {logo.line2 && <span style={{ color: logo.line2_color || '#ffffff' }}>{logo.line2}</span>}
              </span>
            ) : null}
          </span>
        </div>
        {menuItems.map((group) => {
          // menu 속성이 없는 항목은 누구나 접근 가능 (예: 묶음 카탈로그)
          // hidden: true 로 토글된 항목은 사이드바에서 제외
          const visibleItems = group.items.filter(item => item.hidden !== true && (!item.menu || canAccessMenu(item.menu)));
          if (visibleItems.length === 0) return null;

          return (
            <div key={group.id || group.title}>
              <div className="sidebar-section">
                <span className="sidebar-section-label">{group.title}</span>
              </div>
              <ul className="sidebar-menu">
                {visibleItems.map((item) => {
                  const badgeNum = item.badgeKey ? badges[item.badgeKey] : 0;
                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        end={item.path === '/'}
                        className={() => isMenuActive(item.path, location.pathname) ? 'active' : ''}
                        title={showCollapsed ? item.label : ''}
                      >
                        <span className="sidebar-icon-col">
                          <span className="sidebar-icon">{item.icon}</span>
                          {badgeNum > 0 && <span className="icon-badge">{badgeNum > 99 ? '99+' : badgeNum}</span>}
                        </span>
                        <span className="sidebar-label">{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
      })}
        {isAdmin && (
          <ul className="sidebar-menu" style={{ marginTop:'auto' }}>
            <li>
              <button onClick={()=>setShowEditor(true)}
                style={{ display:'flex', alignItems:'center', width:'100%', background:'transparent', border:'none', padding:'8px 0 8px 2px', cursor:'pointer', color:'inherit', font:'inherit', textAlign:'left' }}
                title="사이드바 메뉴 편집 (시스템관리자)">
                <span className="sidebar-icon-col"><span className="sidebar-icon">✏️</span></span>
              </button>
            </li>
          </ul>
        )}
      </div>
      <SidebarEditorModal
        open={showEditor}
        onClose={()=>setShowEditor(false)}
        initialConfig={menuItems}
        onSaved={handleSaved}
        initialLogo={logo}
        onLogoSaved={setLogo}
      />
    </nav>
  );
}
